import {
  type BaseQueryFn,
  createApi,
  type FetchArgs,
  fetchBaseQuery,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

import { createKnownError } from '@/configuration/errorHandler/createKnownError';
import { handleKnownError } from '@/configuration/errorHandler/handleKnownError';
import { isEHttpErrorCode } from '@/configuration/errorHandler/isHttpErrorCode';
import { EDomainErrorCode, EHttpErrorCode, TKnownError } from '@/configuration/errorHandler/types';
import routes from '@/routes/routes';
import Logger from '@/utils/logger';
import { prepareHeaders } from '@/utils/prepareHeaders';

import { getHttpErrorMessage } from './utils';

const LOGGER_TAG = 'Network';

// INFO: Creates reusable base query
const rawBaseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_BASE_BACKEND_URL,
  prepareHeaders,
  // INFO: Establishes the ALB session cookie that /events/ then relies on.
  credentials: 'include',
});

// INFO: Factory that wraps any fetchBaseQuery instance with global error handling
export const createBaseQueryWithErrorHandling =
  (
    baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
  ): BaseQueryFn<string | FetchArgs, unknown, TKnownError> =>
  async (args, api, extraOptions) => {
    const result = await baseQuery(args, api, extraOptions);

    Logger.trace(LOGGER_TAG, 'Response received: ', result);

    if ('error' in result && result.error) {
      const fetchError = result.error as FetchBaseQueryError;
      const status = fetchError.status;

      if (isEHttpErrorCode(status)) {
        const serverUnavailable = (status as EHttpErrorCode) === EHttpErrorCode.SERVER_ERROR;
        Logger.error(LOGGER_TAG, 'Response known error', fetchError);

        const knownError = createKnownError({
          code: status as unknown as EHttpErrorCode,
          message: getHttpErrorMessage(fetchError.status as EHttpErrorCode),
          originalError: fetchError,
          silent: serverUnavailable,
          redirectTo: serverUnavailable ? routes.error : undefined,
        });

        handleKnownError(knownError);
        return { error: knownError };
      }

      // INFO: Unknown error
      const unknownError = createKnownError({
        code: EDomainErrorCode.UNKNOWN,
        message: 'common.error',
        originalError: fetchError,
      });

      handleKnownError(unknownError);
      return { error: unknownError };
    }

    return result;
  };

// INFO: Wraps RTK Query's baseQuery and acts as a global interceptor / middleware
export const baseQueryWithErrorHandling = createBaseQueryWithErrorHandling(rawBaseQuery);

/**
 * Root RTK Query API slice.
 *
 * Notes:
 * - This is the **single API slice** for the project.
 * - Feature-specific endpoints are added via `injectEndpoints`.
 * - Provides a shared cache, middleware, retries, token configuration and reuse across multiple API slices if needed.
 */
export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithErrorHandling,
  endpoints: () => ({}),
});
